import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Lock, Paperclip, Pin, X } from '../../../icons';
import { Button, FilterChip, SearchInput } from '../../../components/shared';
import { formatDate } from '../../../lib/base-i18n';
import { Combobox } from '../../../components/shared';
import {
  listBoardPosts, markBoardRead, searchAllBoards,
  type BoardDto, type BoardPostListItem, type BoardPostSort, type BoardPrefixDto,
} from '../../../api/boards';
import { BoardThumb } from './BoardThumb';
import { highlightMatch } from './highlight';
import { tBoards, getBoardsLang, subscribeBoardsLang } from '../../../lib/boards-i18n';

/**
 * 글 목록 — 전폭 표 (시안 06, 2026-08-18).
 *
 * 좌측 카드가 사라진 만큼 제목 열이 넓어진다. 말머리는 드롭다운이 아니라 **칩**이라
 * 무엇으로 거를 수 있는지가 보인다.
 *
 * 검색어를 넣으면 같은 표가 **검색 결과**로 바뀐다 — 제목·본문에서 찾고(2026-08-19), 제목의
 * 겹치는 대목을 칠하며 본문에서 맞았으면 그 둘레를 발췌로 한 줄 보여 준다.
 * [이 게시판 / 모든 게시판] 을 고를 수 있고, 통합 검색 결과에는 게시판 꼬리표가 붙는다.
 *
 * 정렬은 최신순·조회순·댓글순이다(2026-08-19). 조회·댓글순은 정렬 키가 계속 변하는 값이라
 * 서버가 커서 대신 offset 으로 넘긴다 — 화면은 `nextCursor` 를 그대로 되돌려 주기만 한다.
 *
 * 고정(핀)된 글은 표 위 **고정 영역**에 걸린다(migration 103). 서버는 첫 장에서 아무 것도
 * 거르지 않았을 때만 고정 배열을 채워 보내므로, 화면도 그때만 영역을 그린다 — 말머리로
 * 거르거나 검색 중이면 고정 글도 목록 안에 표식만 달고 섞여 나온다.
 *
 * 피드형 게시판의 타일에는 **첫 이미지 첨부**를 커버로 건다(서버가 골라 준다 — 새 컬럼 없음).
 * 그림이 없는 글은 그림 자리 없이 글자만 그린다.
 *
 * 안 읽은 글은 제목을 굵게 하고 앞에 점을 찍는다(migration 104). 판정은 서버가 한다 —
 * 내가 쓴 글은 항상 읽음이다. "모두 읽음" 은 서버와 **같은 기간 창** 안에서만 찍는다.
 * NEW 배지는 `createdAt` 이 24시간 안이면 붙이는 **클라이언트 판정**이다(서버 필드 아님).
 *
 * ⚠ 기존 `BoardPostList` 는 한 줄도 건드리지 않는다(플래그 OFF 경로).
 */

const NEW_WINDOW_MS = 24 * 60 * 60 * 1000;

export function isNewPost(createdAt: string, now: number = Date.now()): boolean {
  const t = Date.parse(createdAt);
  return Number.isFinite(t) && now - t < NEW_WINDOW_MS && now - t >= 0;
}

interface BoardWideListProps {
  board: BoardDto;
  prefixes: BoardPrefixDto[];
  /** 목록을 다시 읽어야 할 때 바뀌는 값(글 등록·삭제 후). */
  refreshKey: number;
  onOpen: (postId: string) => void;
  /** 리더의 이전·다음 글은 여기서 읽은 목록을 그대로 쓴다(서버 왕복 없음). */
  onLoaded: (posts: BoardPostListItem[]) => void;
  /** 읽음 상태가 바뀌면(모두 읽음) 게시판 배지를 다시 읽게 한다. */
  onReadChanged?: () => void;
}

export function BoardWideList({ board, prefixes, refreshKey, onOpen, onLoaded, onReadChanged }: BoardWideListProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const [posts, setPosts] = useState<BoardPostListItem[]>([]);
  const [pinned, setPinned] = useState<BoardPostListItem[]>([]);
  const [prefixId, setPrefixId] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [scope, setScope] = useState<'board' | 'all'>('board');
  const [sort, setSort] = useState<BoardPostSort>('latest');

  // 게시판을 바꾸면 필터도 처음으로 돌린다 — 남의 게시판 말머리로 걸러진 빈 목록을 보여주지 않으려는 것.
  useEffect(() => { setPrefixId(''); setQ(''); setScope('board'); setSort('latest'); }, [board.id]);

  const load = useCallback((append: boolean, from: string | null) => {
    let cancelled = false;
    if (!append) setLoading(true);
    const term = q.trim();
    // 통합 검색은 게시판을 넘나드는 별도 질의다 — 말머리 필터·고정 영역은 이 판에 없다.
    const request = scope === 'all' && term
      ? searchAllBoards(term, { ...(from ? { cursor: from } : {}) })
        .then((r) => ({ ...r, pinned: [] as BoardPostListItem[] }))
      : listBoardPosts(board.id, {
        ...(prefixId ? { prefixId } : {}),
        ...(term ? { q: term } : {}),
        ...(sort !== 'latest' ? { sort } : {}),
        ...(from ? { cursor: from } : {}),
      });
    request
      .then((r) => {
        if (cancelled) return;
        setPosts((prev) => (append ? [...prev, ...r.posts] : r.posts));
        // 서버가 아직 고정을 모르는 판(구버전)에서도 화면이 서게 기본값을 둔다.
        if (!append) setPinned(r.pinned ?? []);
        setCursor(r.nextCursor);
        setError(null);
      })
      .catch((e: any) => {
        if (!cancelled) setError(tBoards('boards.post.err_list', { msg: e?.response?.data?.message ?? e?.message ?? 'unknown' }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [board.id, prefixId, q, scope, sort]);

  useEffect(() => {
    // 검색어는 입력 중 계속 바뀌므로 살짝 늦춘다. stale-response 는 cancelled 로 막는다.
    const handle = setTimeout(() => { load(false, null); }, q.trim() ? 250 : 0);
    return () => clearTimeout(handle);
  }, [load, refreshKey, q]);

  useEffect(() => { onLoaded(posts); }, [posts, onLoaded]);

  async function markAllRead() {
    setMarking(true);
    try {
      await markBoardRead(board.id);
      onReadChanged?.();
    } catch (e: any) {
      setError(tBoards('boards.wide.mark_all_read_err', { msg: e?.response?.data?.message ?? e?.message ?? 'unknown' }));
    } finally {
      setMarking(false);
    }
  }

  const isFeed = board.boardType === 'FEED';
  const hasUnread = posts.some((p) => !p.isRead) || pinned.some((p) => !p.isRead);
  const term = q.trim();
  const searching = term.length > 0;
  const countLabel = cursor
    ? tBoards('boards.wide.search_more', { n: posts.length })
    : tBoards('boards.wide.search_count', { n: posts.length });

  return (
    <section className="bdw-list" aria-label={board.name}>
      {/*
        필터 · 정렬 · 검색을 **한 줄**로 (2026-08-19).
        말머리가 늘면 줄이 접혀 목록이 아래로 밀렸다. 이제 말머리 칩만 가로로 흐르고
        (넘치면 그 안에서 스크롤) 정렬·모두읽음·검색은 오른쪽에 고정된다.
        ⚠ 흐르는 칸에 `min-width: 0` 이 없으면 flex 가 안 줄어들어 옆칸을 밀어낸다.
      */}
      <div className="bdw-tools">
        <div className="bdw-tools-chips" role="group" aria-label={tBoards('boards.wide.filter_aria')}>
          {/* 정본 D14 「고르기 칩」 — 눌러서 거르는 것이라 `aria-pressed` 를 진다. */}
          <FilterChip pressed={prefixId === ''} onClick={() => setPrefixId('')}>
            {tBoards('boards.wide.filter_all')}
          </FilterChip>
          {prefixes.map((p) => (
            <FilterChip key={p.id} pressed={prefixId === p.id} onClick={() => setPrefixId(p.id)}>
              {p.label}
            </FilterChip>
          ))}
        </div>

        {/* 통합 검색 결과는 서버가 최신순 하나로만 준다 — 그 판에서는 정렬을 감춘다. */}
        {!(searching && scope === 'all') && (
          <Combobox
            value={sort}
            onChange={(v) => setSort(v as BoardPostSort)}
            options={[
              { value: 'latest', label: tBoards('boards.wide.sort_latest') },
              { value: 'views', label: tBoards('boards.wide.sort_views') },
              { value: 'comments', label: tBoards('boards.wide.sort_comments') },
            ]}
            ariaLabel={tBoards('boards.wide.sort_aria')}
            className="bdw-sort"
          />
        )}

        {hasUnread && (
          <Button
            variant="secondary"
            className="bdw-readall"
            disabled={marking}
            onClick={markAllRead}
          >
            {tBoards('boards.wide.mark_all_read')}
          </Button>
        )}

        {/* 손으로 짠 검색칸 → 정본 SearchInput(돋보기 · 지우기 · Esc 가 딸려 온다). */}
        <SearchInput
          wrapperClassName="bdw-search"
          value={q}
          placeholder={tBoards('boards.post.search')}
          aria-label={tBoards('boards.post.search')}
          onChange={(e) => setQ(e.target.value)}
          onClear={() => setQ('')}
        />
      </div>

      {pinned.length > 0 && (
        <section className="bdw-pins" aria-label={tBoards('boards.wide.pinned_section')}>
          {pinned.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`bdw-pin${p.isRead ? '' : ' bdw-pin--unread'}`}
              onClick={() => onOpen(p.id)}
            >
              <Pin size={14} aria-hidden />
              <span className="bdw-pin-txt">
                <b title={p.title}>{p.title}</b>
                <span className="bdw-pin-meta">
                  {tBoards('boards.post.meta', {
                    author: p.author.name,
                    date: formatDate(p.createdAt),
                    views: p.viewCount,
                  })}
                </span>
              </span>
            </button>
          ))}
        </section>
      )}

      {searching && (
        <div className="bdw-srhd">
          <h3>{tBoards('boards.wide.search_result', { q: term })}</h3>
          <span className="bdw-srcount bd-num">{loading ? '…' : countLabel}</span>
          <span className="bdw-srscope">{tBoards('boards.wide.search_scope')}</span>
          <span className="bdw-srseg" role="group" aria-label={tBoards('boards.wide.search_scope')}>
            <button
              type="button"
              aria-pressed={scope === 'board'}
              onClick={() => setScope('board')}
            >
              {tBoards('boards.wide.search_here')}
            </button>
            <button
              type="button"
              aria-pressed={scope === 'all'}
              onClick={() => setScope('all')}
            >
              {tBoards('boards.wide.search_all')}
            </button>
          </span>
          <Button
            variant="secondary"
            onClick={() => { setQ(''); setScope('board'); }}
            iconLeft={<X size={13} aria-hidden />}
          >
            {tBoards('boards.wide.search_clear')}
          </Button>
        </div>
      )}

      {error && <p className="bd-error" role="alert">{error}</p>}

      {posts.length === 0 ? (
        <div className="bdw-empty">
          {loading ? '…' : (searching ? tBoards('boards.wide.search_empty') : tBoards('boards.post.empty'))}
        </div>
      ) : isFeed ? (
        <div className="bdw-tiles">
          {posts.map((p) => (
            <button key={p.id} type="button" className="bdw-tile" onClick={() => onOpen(p.id)}>
              {p.thumbnail && <BoardThumb fileId={p.thumbnail.fileId} name={p.thumbnail.name} />}
              <span className="bdw-tile-head">
                {p.prefix && <span className="bdw-pill">{p.prefix.label}</span>}
                {isNewPost(p.createdAt) && <span className="bdw-pill bdw-pill--new">{tBoards('boards.wide.new_badge')}</span>}
                {p.locked && <Lock size={12} aria-hidden />}
                {p.attachmentCount > 0 && <Paperclip size={12} aria-hidden />}
              </span>
              <span className="bdw-tile-name" title={p.title}>
                {searching ? highlightMatch(p.title, term) : p.title}
              </span>
              <span className="bdw-tile-meta">
                {tBoards('boards.post.meta', {
                  author: p.author.name,
                  date: formatDate(p.createdAt),
                  views: p.viewCount,
                })}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="bdw-table" role="table">
          <div className="bdw-thead" role="row">
            <span role="columnheader">{tBoards('boards.col.prefix')}</span>
            <span role="columnheader">{tBoards('boards.col.title')}</span>
            <span className="bdw-cell-author" role="columnheader">{tBoards('boards.col.author')}</span>
            <span className="bdw-cell-date" role="columnheader">{tBoards('boards.col.date')}</span>
            <span className="bdw-cell-num" role="columnheader">{tBoards('boards.col.views')}</span>
            <span className="bdw-cell-num bdw-cell-comments" role="columnheader">{tBoards('boards.col.comments')}</span>
          </div>

          {posts.map((p) => (
            <button
              key={p.id}
              type="button"
              role="row"
              className={`bdw-row${p.locked ? ' bdw-row--locked' : ''}${p.isRead ? '' : ' bdw-row--unread'}${p.excerpt ? ' bdw-row--excerpt' : ''}`}
              onClick={() => onOpen(p.id)}
            >
              <span role="cell">
                {p.prefix ? <span className="bdw-pill">{p.prefix.label}</span> : <span className="bdw-dash">—</span>}
              </span>

              <span className="bdw-cell-title" role="cell">
                <span className="bdw-title-line">
                  {!p.isRead && (
                    <span className="bdw-unread-dot" title={tBoards('boards.wide.unread_dot')} />
                  )}
                  {p.isPinned && (
                    <Pin size={13} aria-hidden className="bdw-pin-mark" />
                  )}
                  {p.locked && <Lock size={13} aria-hidden className="bdw-lock" />}
                  <b title={p.title}>{searching ? highlightMatch(p.title, term) : p.title}</b>
                  {p.board && <span className="bdw-pill">{p.board.name}</span>}
                  {p.attachmentCount > 0 && (
                    <span className="bdw-att" title={tBoards('boards.post.attachments', { n: p.attachmentCount })}>
                      <Paperclip size={12} aria-hidden />
                      <b className="bd-num">{p.attachmentCount}</b>
                    </span>
                  )}
                  {isNewPost(p.createdAt) && (
                    <span className="bdw-pill bdw-pill--new">{tBoards('boards.wide.new_badge')}</span>
                  )}
                </span>
                {/* 본문에서 맞았을 때만 온다 — 제목만 맞은 글에 같은 문장을 두 번 보이지 않게. */}
                {p.excerpt && (
                  <span className="bdw-excerpt">{highlightMatch(p.excerpt, term)}</span>
                )}
              </span>

              <span className="bdw-cell-author" role="cell">{p.author.name}</span>
              <span className="bdw-cell-date" role="cell">{formatDate(p.createdAt)}</span>
              <span className="bdw-cell-num bd-num" role="cell">{p.viewCount}</span>
              <span className="bdw-cell-num bdw-cell-comments bd-num" role="cell">
                {p.commentCount ? p.commentCount : <span className="bdw-dash">—</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {cursor && (
        <div className="bdw-more">
          <Button variant="secondary" onClick={() => load(true, cursor)}>
            {tBoards('boards.post.more')}
          </Button>
        </div>
      )}
    </section>
  );
}
